/**
 * El cliente del inbox del candidato — habla con el mount `/job-apply` de
 * job-apply-service (`GET /api/threads`, `/api/threads/meta`,
 * `/api/threads/:applicationId`, `POST …/reply` y `POST …/suggest-reply`).
 *
 * DECISIÓN DE ARQUITECTURA, la misma que está escrita del lado del servidor: el
 * inbox pertenece al servicio extraído y nada de esto se replica al monolito ni
 * se importa desde él. Los dos canales llegan con la MISMA forma: en domain los
 * mensajes salen de nuestra base; en gmail el servidor lee el hilo de la casilla
 * del candidato a demanda y la respuesta sale desde su Gmail. El canal es el del
 * HILO (`send_channel`), no el de la membresía. Responder desde acá existe
 * cuando `can_reply` es true; si Gmail no se pudo leer llega `gmail_error`.
 *
 * Por qué una clase de error y no un `Error(mensaje)` como en las pantallas
 * viejas: la respuesta a un hilo falla de seis maneras distintas que la UI tiene
 * que tratar distinto (el 409 de gmail manda a Gmail, el 429 dice cuánto
 * esperar, el 502 significa que el correo NO salió y hay que conservar el
 * texto). Sin el `code` y el status no se puede escribir ninguna de esas frases.
 */
import { apiFetch } from '~/lib/api';

export type MessageDirection = 'inbound' | 'outbound';
export type SendChannel = 'gmail' | 'domain';

/**
 * Por qué no se pudo leer un hilo de Gmail:
 *   - `gmail_reconnect_required`: el permiso de Google se venció o se revocó;
 *   - `gmail_unavailable`: Gmail no contestó (pasajero);
 *   - `gmail_thread_missing`: el hilo ya no está en la casilla.
 */
export type GmailThreadError = 'gmail_reconnect_required' | 'gmail_unavailable' | 'gmail_thread_missing';

export interface ThreadSummary {
  /** El backend manda `_id` === `application_id`: un hilo ES una application. */
  _id: string;
  application_id: string;
  status: string;
  subject: string;
  company: string;
  title: string;
  location: string;
  send_channel: SendChannel;
  sent_at: string | null;
  last_message_at: string;
  last_message_preview: string;
  last_message_direction: MessageDirection;
  unread_count: number;
  /** Ya incluye el primer mensaje sintetizado: nunca se le suma uno acá. */
  message_count: number;
  /**
   * La respuesta de la empresa parece una oportunidad (el clasificador del
   * servidor: interés, entrevista, piden datos). Opcional: un payload viejo no
   * lo trae y cuenta como false.
   */
  reply_is_opportunity?: boolean;
}

export interface ThreadMessage {
  _id: string;
  /** true en el correo de la postulación, que no existe como fila. */
  synthesized: boolean;
  direction: MessageDirection;
  from_address: string;
  to_address: string;
  subject: string;
  body_text: string;
  body_html: string | null;
  created_at: string;
  read_at: string | null;
}

export interface ThreadDetail {
  _id: string;
  application_id: string;
  status: string;
  subject: string;
  company: string;
  title: string;
  location: string;
  contact_email: string;
  send_channel: SendChannel;
  /**
   * La señal para la UI: true → compositor, false → link a Gmail.
   *
   * Es false también cuando la membresía ya está en el canal domain pero ESTE
   * hilo salió por Gmail (se postuló antes de conectar el dominio): contestarlo
   * desde `slug@dominio` le llegaría a la empresa como un desconocido. El POST
   * de respuesta aplica la misma regla, así que el compositor nunca aparece
   * sobre algo que el servidor va a rechazar.
   */
  can_reply: boolean;
  /**
   * A quién le llega la respuesta. En gmail es quien contestó por última vez (su
   * Reply-To), que muchas veces no es el correo de la vacante. Opcional: un
   * payload viejo no lo trae y el compositor cae a `contact_email`.
   */
  reply_to?: string;
  /** Hilo gmail: la dirección desde la que sale la respuesta. null en domain. */
  gmail_address?: string | null;
  /** Hilo gmail que no se pudo leer. null/ausente = todo bien (o domain). */
  gmail_error?: GmailThreadError | null;
  sent_at: string | null;
  /** Cuántos inbound quedaron leídos al abrir. > 0 ⇒ el badge del nav cambió. */
  marked_read: number;
  /**
   * Sólo tiene valor en hilos del canal gmail, y aun ahí puede faltar (una
   * postulación vieja enviada antes de que se guardara el id del hilo). Opcional
   * a propósito: `gmailThreadUrl` cae a una búsqueda por asunto cuando no está.
   */
  gmail_thread_id?: string | null;
  /** Igual que en `ThreadSummary`: la etiqueta «Oportunidad» del encabezado. */
  reply_is_opportunity?: boolean;
}

export interface ThreadsPage {
  threads: ThreadSummary[];
  total: number;
  limit: number;
  offset: number;
  /** Sólo cuando la lista no pudo leer Gmail: los hilos salen igual, sin sus respuestas. */
  gmail_error?: GmailThreadError;
}

export interface ThreadsMeta {
  /**
   * HILOS con respuestas sin leer, no mensajes sueltos: tres respuestas de la
   * misma empresa cuentan UNA. Es lo que hace que el badge del nav
   * (`total_unread + pending_invitations`) valga exactamente lo que muestra el
   * filtro "No leídos" del inbox, que también lista hilos.
   */
  total_unread: number;
  pending_invitations: number;
}

/** La invitación de agencia, tal como la sirve `GET /api/invitations`. */
export interface CandidateInvitation {
  _id: string;
  status: string;
  invited_at: string | null;
  responded_at: string | null;
  agency: { name: string; country: string | null };
  position: { _id: string; title: string; company: string; location: string };
}

export class ThreadApiError extends Error {
  status: number;
  /** El `code` del cuerpo: 'gmail_channel', 'rate_limited', 'bounced'… */
  code: string;
  retryAfterSeconds: number | null;

  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body?.error === 'string' ? body.error : `HTTP ${status}`);
    this.name = 'ThreadApiError';
    this.status = status;
    this.code = typeof body?.code === 'string' ? body.code : '';
    this.retryAfterSeconds =
      typeof body?.retry_after_seconds === 'number' ? body.retry_after_seconds : null;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers as Record<string, string>) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ThreadApiError(res.status, body ?? {});
  }
  return (await res.json()) as T;
}

/** El backend ya devuelve `last_message_at` desc: la página NO se reordena acá. */
export function listThreads(limit = 20, offset = 0): Promise<ThreadsPage> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return request<ThreadsPage>(`/job-apply/api/threads?${params}`);
}

export function fetchThreadsMeta(): Promise<ThreadsMeta> {
  return request<ThreadsMeta>('/job-apply/api/threads/meta');
}

export function fetchThread(applicationId: string): Promise<{ thread: ThreadDetail; messages: ThreadMessage[] }> {
  return request(`/job-apply/api/threads/${encodeURIComponent(applicationId)}`);
}

/**
 * Responde un hilo.
 *
 * `to` es el destinatario que el compositor le MOSTRÓ al candidato ("Le llega
 * a"): en un hilo de Gmail el servidor sólo envía si sigue siendo uno de los
 * permitidos, y si cambió responde 409 `recipient_changed` para que la pantalla
 * recargue y lo muestre. `clientReplyId` es el mismo en cada reintento del
 * mismo envío: con él un corte de red no termina en dos correos a la empresa.
 */
export async function replyToThread(
  applicationId: string,
  bodyText: string,
  options: { to?: string; clientReplyId?: string } = {},
): Promise<ThreadMessage> {
  const body = await request<{ message: ThreadMessage }>(
    `/job-apply/api/threads/${encodeURIComponent(applicationId)}/reply`,
    {
      method: 'POST',
      body: JSON.stringify({
        body_text: bodyText,
        ...(options.to ? { to: options.to } : {}),
        ...(options.clientReplyId ? { client_reply_id: options.clientReplyId } : {}),
      }),
    },
  );
  return body.message;
}

/** La tipología de la respuesta de la empresa. Lista cerrada, la misma del servidor. */
export type ReplyTypology =
  | 'interest'
  | 'interview'
  | 'info_request'
  | 'locals_only'
  | 'rejection'
  | 'other';

/**
 * Qué puede contestar el candidato: la lista cerrada `REPLY_OPTIONS` del
 * servidor, por id. Las etiquetas viven en i18n (`suggestion.replyOptions`).
 */
export type ReplyOptionId =
  | 'interview_confirm'
  | 'interview_reschedule'
  | 'interview_details'
  | 'info_send'
  | 'info_clarify'
  | 'interest_confirm'
  | 'interest_question'
  | 'locals_visa_next_season'
  | 'locals_keep_on_file'
  | 'locals_transfer'
  | 'rejection_future';

export interface ReplySuggestionDraft {
  /** Qué hace esta versión, en el idioma de la interfaz ("Preguntar por la próxima temporada"). */
  label: string;
  /** El correo, en el idioma de la EMPRESA (casi siempre inglés). */
  body: string;
}

export interface ReplySuggestion {
  /** El mensaje entrante al que contesta: si llega otro, la sugerencia ya no vale. */
  message_id: string;
  typology: ReplyTypology;
  /** Para el candidato, en el idioma de su interfaz: qué le dicen y qué conviene. */
  explanation: string;
  employer_language: string;
  drafts: ReplySuggestionDraft[];
  generated_at: string;
}

export interface SuggestReplyResult {
  suggestion: ReplySuggestion;
  cached: boolean;
  /** La opción para la que se escribieron los borradores; null = sin opción. */
  option?: ReplyOptionId | null;
  /** Las opciones que se pueden tocar para este mensaje (vacío en `other`). */
  options?: ReplyOptionId[];
  /** La tipología con que se eligieron las opciones: la de Jev si la hay. */
  options_typology?: ReplyTypology;
}

/**
 * La respuesta sugerida al último mensaje de la empresa. NO envía nada: el
 * borrador va a la caja de respuesta y la persona decide.
 *
 * El servidor la guarda por mensaje (y por opción), así que pedirla al abrir el
 * hilo no regenera; `refresh` pide otra tanda (eso sí gasta cupo: 400/409/429/502
 * llegan como `ThreadApiError` con su `code`). `option` pide borradores escritos
 * para una de las opciones que devolvió la llamada anterior.
 */
export async function suggestReply(
  applicationId: string,
  options: { refresh?: boolean; option?: ReplyOptionId } = {},
): Promise<SuggestReplyResult> {
  return request(`/job-apply/api/threads/${encodeURIComponent(applicationId)}/suggest-reply`, {
    method: 'POST',
    body: JSON.stringify({
      ...(options.refresh ? { refresh: true } : {}),
      ...(options.option ? { option: options.option } : {}),
    }),
  });
}

/**
 * `status=all`: el inbox muestra las pendientes arriba, pero el filtro
 * "Invitaciones" también enseña las ya contestadas como historial.
 */
export async function listInvitations(): Promise<CandidateInvitation[]> {
  const body = await request<{ invitations?: CandidateInvitation[] }>(
    '/job-apply/api/invitations?status=all',
  );
  return Array.isArray(body?.invitations) ? body.invitations : [];
}
